import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Radio,
  Tooltip,
  Alert,
  Divider,
  Space,
  Tag,
} from 'antd';
import {
  CheckCircleFilled, MinusCircleOutlined,
  InfoCircleOutlined, SearchOutlined,
  FileTextOutlined, LinkOutlined, PaperClipOutlined, EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import axiosInstance from '../../variables/api/axiosInstance';
import { CHARTE_COLORS, PV_PROVENANCES, PV_SENS_CIRCULATION } from '../../variables/constants';
import GedFileField from '../../components/GedFileField';
import CBModal from '../../components/CBModal';
import { ChampNumeroPv, etatDepuisNumeroPv, nettoyerNumeroPv } from './numeroPv';
import { erreurChronologie } from './chronologiePv';
import { useTranslation, Trans } from 'react-i18next';

const { Option } = Select;
const { TextArea } = Input;

/* Sens de circulation déduit du type de sinistre - même correspondance que côté
   backend : véhicule étranger accidenté au Togo = ET, véhicule togolais
   accidenté à l'étranger = TE. */
const SENS_PAR_TYPE_SINISTRE = {
  SURVENU_TOGO: 'ET',
  SURVENU_ETRANGER: 'TE',
};

/* ── Carte radio OK / NEANT ── */
const ElementPvRadio = ({ label, hint, value, onChange }) => {
  const { t } = useTranslation('pvSinistres');
  const isOk = value === 'OK';
  const isNeant = value === 'NEANT';

  return (
    <div
      style={{
        border: '1px solid var(--cb-border)',
        borderRadius: 10,
        padding: '14px 16px',
        background: isOk ? CHARTE_COLORS.greenPale : isNeant ? '#fef2f2' : '#fff',
        transition: 'all 180ms ease',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{label}</div>
          {hint && (
            <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>{hint}</div>
          )}
        </div>
        <Radio.Group
          value={value}
          onChange={(e) => onChange(e.target.value)}
          buttonStyle="solid"
          size="small"
        >
          <Radio.Button
            value="OK"
            style={isOk ? { background: CHARTE_COLORS.green, borderColor: CHARTE_COLORS.green, color: '#fff' } : {}}
          >
            <CheckCircleFilled style={{ marginRight: 4 }} />
            {t('commun.present')}
          </Radio.Button>
          <Radio.Button value="NEANT">
            <MinusCircleOutlined style={{ marginRight: 4 }} />
            {t('commun.neant')}
          </Radio.Button>
        </Radio.Group>
      </div>
    </div>
  );
};

/* ── Bannière indicateur PV complet ── */
const CompletudeBanner = ({ aCirconstances, aAuditions, aCroquis }) => {
  const { t } = useTranslation('pvSinistres');
  const elements = [
    { key: 'circ', label: t('commun.circonstances'), ok: aCirconstances === 'OK' },
    { key: 'aud', label: t('commun.auditions'), ok: aAuditions === 'OK' },
    { key: 'cro', label: t('commun.croquis'), ok: aCroquis === 'OK' },
  ];
  const nbOk = elements.filter(e => e.ok).length;
  const estComplet = nbOk === 3;

  return (
    <div
      style={{
        background: estComplet ? CHARTE_COLORS.green_PALE : '#fff7ed',
        border: `1px solid ${estComplet ? CHARTE_COLORS.green : '#fdba74'}`,
        borderRadius: 10,
        padding: '12px 16px',
        marginBottom: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: estComplet ? CHARTE_COLORS.green : '#9a3412', display: 'flex', alignItems: 'center', gap: 6 }}>
          {estComplet ? <CheckCircleFilled /> : <InfoCircleOutlined />}
          {estComplet ? t('nouveau.completude.complet') : t('nouveau.completude.incomplet', { nbOk })}
        </div>
        <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 2 }}>
          {estComplet
            ? t('nouveau.completude.completAide')
            : t('nouveau.completude.incompletAide')}
        </div>
      </div>
      <Space size={6}>
        {elements.map(e => (
          <Tag
            key={e.key}
            color={e.ok ? 'CHARTE_COLORS.green' : 'default'}
            style={{ borderRadius: 20, fontSize: 11, margin: 0 }}
          >
            {e.ok ? <CheckCircleFilled style={{ marginRight: 4 }} /> : <MinusCircleOutlined style={{ marginRight: 4 }} />}
            {e.label}
          </Tag>
        ))}
      </Space>
    </div>
  );
};

/* ── Modal principal ── */
export default function NouveauPvModal({ open, onClose, onCreated, defaultSinistreId, replaceFromPv = null }) {
  const { t } = useTranslation('pvSinistres');
  const isReplace = Boolean(replaceFromPv);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [entites, setEntites] = useState([]);
  const [sinistresOptions, setSinistresOptions] = useState([]);
  const [searchSinistre, setSearchSinistre] = useState('');
  const [fichier, setFichier] = useState(null);
  const [motifRemplacement, setMotifRemplacement] = useState('');

  const aCirconstances = Form.useWatch('aCirconstances', form);
  const aAuditions = Form.useWatch('aAuditions', form);
  const aCroquis = Form.useWatch('aCroquis', form);
  const sinistreTrackingId = Form.useWatch('sinistreTrackingId', form);

  const estComplet = useMemo(
    () => aCirconstances === 'OK' && aAuditions === 'OK' && aCroquis === 'OK',
    [aCirconstances, aAuditions, aCroquis]
  );

  // Le sens de circulation découle du type de sinistre (SURVENU_TOGO → ET,
  // SURVENU_ETRANGER → TE). Dès qu'un sinistre est associé on le pré-remplit et
  // on verrouille le champ : il reste visible pour que l'agent voie la valeur
  // retenue. Il n'est saisi à la main que pour les PV enregistrés « à l'aveugle »,
  // avant rattachement à un dossier.
  const typeSinistreAssocie = sinistresOptions
    .find(s => s.sinistreTrackingId === sinistreTrackingId)?.typeSinistre;
  const sensDerive = Boolean(sinistreTrackingId) && Boolean(SENS_PAR_TYPE_SINISTRE[typeSinistreAssocie]);
  /* TE : le PV est dressé à l'étranger par une autorité absente du référentiel -
     la liste déroulante cède la place à une saisie libre. Auto dès que le
     sinistre associé est un TE, débrayable pour les cas particuliers. */
  const [pvEtranger, setPvEtranger] = useState(false);
  useEffect(() => {
    setPvEtranger(typeSinistreAssocie === 'SURVENU_ETRANGER');
  }, [typeSinistreAssocie]);

  useEffect(() => {
    const sens = SENS_PAR_TYPE_SINISTRE[typeSinistreAssocie];
    if (sinistreTrackingId && sens) form.setFieldsValue({ sensCirculation: sens });
  }, [sinistreTrackingId, typeSinistreAssocie, form]);

  /* Charger les unités rédactrices (police/gendarmerie) + sinistres (recherche) */
  useEffect(() => {
    if (!open) return;
    axiosInstance.get('/v1/entites-constat').then(r => {
      setEntites(Array.isArray(r.data?.data) ? r.data.data : []);
    }).catch(() => setEntites([]));
  }, [open]);

  useEffect(() => {
    if (!open || !searchSinistre || searchSinistre.length < 2) {
      setSinistresOptions([]);
      return;
    }
    const t = setTimeout(() => {
      axiosInstance
        .get(`/v1/sinistres?search=${encodeURIComponent(searchSinistre)}&size=10`)
        .then(r => {
          setSinistresOptions(Array.isArray(r.data?.data) ? r.data.data : []);
        })
        .catch(() => setSinistresOptions([]));
    }, 300);
    return () => clearTimeout(t);
  }, [searchSinistre, open]);

  /* Reset à l'ouverture + pré-remplissage si sinistreId fourni */
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setMotifRemplacement('');
    setFichier(null);

    if (isReplace) {
      // Pré-remplit depuis l'ancien PV
      form.setFieldsValue({
        numeroPv: replaceFromPv.numeroPv,
        etatNumeroPv: etatDepuisNumeroPv(replaceFromPv.numeroPv),
        sensCirculation: replaceFromPv.sensCirculation,
        entiteConstatTrackingId: replaceFromPv.entiteConstatTrackingId,
        lieuAccident: replaceFromPv.lieuAccident,
        dateAccidentPv: replaceFromPv.dateAccidentPv ? dayjs(replaceFromPv.dateAccidentPv) : null,
        dateEtablissementPv: replaceFromPv.dateEtablissementPv ? dayjs(replaceFromPv.dateEtablissementPv) : null,
        dateReceptionBncb: dayjs(),
        provenance: replaceFromPv.provenance,
        detailProvenance: replaceFromPv.detailProvenance,
        referenceSinistreLiee: replaceFromPv.referenceSinistreLiee,
        sinistreTrackingId: replaceFromPv.sinistreTrackingId,
        aCirconstances: replaceFromPv.aCirconstances || 'NEANT',
        aAuditions: replaceFromPv.aAuditions || 'NEANT',
        aCroquis: replaceFromPv.aCroquis || 'NEANT',
        remarques: replaceFromPv.remarques,
      });
      if (replaceFromPv.sinistreTrackingId) {
        axiosInstance.get(`/v1/sinistres/${replaceFromPv.sinistreTrackingId}`).then(r => {
          const s = r.data?.data; if (s) setSinistresOptions([s]);
        }).catch(() => { });
      }
      return;
    }

    form.setFieldsValue({
      dateReceptionBncb: dayjs(),
      aCirconstances: 'NEANT',
      aAuditions: 'NEANT',
      aCroquis: 'NEANT',
    });

    if (defaultSinistreId) {
      // Charger les infos du sinistre pour afficher le bon libellé dans le select
      axiosInstance.get(`/v1/sinistres/${defaultSinistreId}`).then(r => {
        const s = r.data?.data;
        if (!s) return;
        setSinistresOptions([s]);
        // Le sens de circulation est posé par l'effet de dérivation ci-dessus.
        form.setFieldsValue({ sinistreTrackingId: defaultSinistreId });
      }).catch(() => { });
    }
  }, [open, form, defaultSinistreId, isReplace, replaceFromPv]);

  /* Soumission */
  const onFinish = async (vals) => {
    setLoading(true);
    try {
      const payload = {
        // Filet : le prefixe reste retire meme tape ou colle dans le champ,
        // sans quoi le PV ne se retrouverait plus a la recherche.
        numeroPv: nettoyerNumeroPv(vals.numeroPv),
        sensCirculation: vals.sensCirculation,
        entiteConstatTrackingId: pvEtranger ? null : vals.entiteConstatTrackingId,
        entiteConstatExterne: pvEtranger ? (vals.entiteConstatExterne?.trim() || null) : null,
        lieuAccident: vals.lieuAccident?.trim(),
        dateAccidentPv: vals.dateAccidentPv?.format('YYYY-MM-DD'),
        dateEtablissementPv: vals.dateEtablissementPv?.format('YYYY-MM-DD') ?? null,
        dateReceptionBncb: vals.dateReceptionBncb?.format('YYYY-MM-DD'),
        provenance: vals.provenance ?? null,
        detailProvenance: vals.detailProvenance?.trim() || null,
        referenceSinistreLiee: vals.referenceSinistreLiee?.trim() || null,
        sinistreTrackingId: vals.sinistreTrackingId ?? null,
        aCirconstances: vals.aCirconstances,
        aAuditions: vals.aAuditions,
        aCroquis: vals.aCroquis,
        remarques: vals.remarques?.trim() || null,
      };

      // ── Mode REMPLACEMENT : endpoint dédié, multipart unique ──
      if (isReplace) {
        if (!motifRemplacement.trim()) {
          toast.error(t('nouveau.remplacement.motifRequis'));
          setLoading(false);
          return;
        }
        const fd = new FormData();
        fd.append('data', new Blob([JSON.stringify(payload)], { type: 'application/json' }));
        if (fichier) fd.append('file', fichier);
        fd.append('motif', motifRemplacement.trim());
        const resR = await axiosInstance.post(
          `/v1/pv/${replaceFromPv.pvTrackingId}/remplacer`, fd,
          { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success(resR.data?.message || t('nouveau.remplacement.succes', { numero: payload.numeroPv }));
        onCreated?.(resR.data?.data);
        onClose();
        return;
      }

      // 1. Création du PV
      const res = await axiosInstance.post('/v1/pv', payload);
      let pvFinal = res.data?.data;
      const pvTrackingId = pvFinal?.pvTrackingId;

      // 2. Upload du fichier scanné si fourni - backend stocke directement dans le dossier GED
      //    du sinistre si associé (sinon fallback local).
      if (fichier && pvTrackingId) {
        const fd = new FormData();
        fd.append('file', fichier);
        fd.append('titre', `PV ${payload.numeroPv}`);
        fd.append('typeDocument', 'PV');
        try {
          const uploadRes = await axiosInstance.post(`/v1/pv/${pvTrackingId}/document`, fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          pvFinal = uploadRes.data?.data || pvFinal;
        } catch (e) {
          toast.warning(t('nouveau.messages.echecTeleversement'));
        }
      }

      const gedReady = Boolean(pvFinal?.ossanGedDocumentId);
      toast.success(
        gedReady
          ? (estComplet ? t('nouveau.messages.archiveGed', { numero: payload.numeroPv }) : t('nouveau.messages.archiveGedIncomplet', { numero: payload.numeroPv }))
          : (estComplet ? t('nouveau.messages.enregistre', { numero: payload.numeroPv }) : t('nouveau.messages.enregistreIncomplet', { numero: payload.numeroPv }))
      );
      onCreated?.(pvFinal);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('nouveau.messages.erreurEnregistrement'));
    } finally {
      setLoading(false);
    }
  };

  /* Chronologie des trois dates : l'accident survient, l'entite de constat dresse
     le PV, le bureau le recoit. La regle vit dans chronologiePv.js, ou elle est
     testee ; ici on ne fait que lui passer les valeurs du formulaire. */
  const iso = (d) => (d ? d.format('YYYY-MM-DD') : null);

  const validerChronologie = () => {
    const message = erreurChronologie({
      accident: iso(form.getFieldValue('dateAccidentPv')),
      etablissement: iso(form.getFieldValue('dateEtablissementPv')),
      reception: iso(form.getFieldValue('dateReceptionBncb')),
    });
    return message ? Promise.reject(new Error(message)) : Promise.resolve();
  };

  return (
    <CBModal
      title={
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            {isReplace
              ? t('nouveau.titreRemplacement', { numero: replaceFromPv?.numeroPv || '' })
              : t('nouveau.titre')}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 400, marginTop: 2 }}>
            {isReplace
              ? t('nouveau.sousTitreRemplacement')
              : t('nouveau.sousTitre')}
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
      styles={{ body: { paddingTop: 16, maxHeight: '75vh', overflowY: 'auto' } }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark="optional"
      >
        {/* ────────── Indicateur complétude ────────── */}
        <CompletudeBanner
          aCirconstances={aCirconstances}
          aAuditions={aAuditions}
          aCroquis={aCroquis}
        />

        {/*  Section 1 - Identification  */}
        <div className="cb-form-section-title"><FileTextOutlined style={{ marginRight: 6 }} />{t('nouveau.sections.identification')}</div>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            {/* Lisible / Illisible / Inconnu, et « N° » en addon : il n'est pas du texte
                saisi et ne part donc pas dans la valeur enregistrée. */}
            <ChampNumeroPv
              form={form}
              tooltip={t('nouveau.champs.numeroAide')}
            />
          </Col>
          <Col xs={24} md={12}>
            {pvEtranger ? (
              <Form.Item
                name="entiteConstatExterne"
                label={t('nouveau.champs.autoriteEtrangere')}
                rules={[{ required: true, whitespace: true, message: t('nouveau.champs.autoriteEtrangereRequise') }]}
                tooltip={t('nouveau.champs.autoriteEtrangereAide')}
              >
                <Input placeholder={t('nouveau.champs.autoriteEtrangerePlaceholder')} maxLength={200} />
              </Form.Item>
            ) : (
              <Form.Item
                name="entiteConstatTrackingId"
                label={t('nouveau.champs.entite')}
                rules={[{ required: true, message: t('nouveau.champs.entiteRequise') }]}
                tooltip={t('nouveau.champs.entiteAide')}
              >
                <Select
                  showSearch
                  placeholder={t('nouveau.champs.entitePlaceholder')}
                  optionFilterProp="label"
                  notFoundContent={entites.length === 0 ? t('nouveau.champs.aucuneEntite') : t('nouveau.champs.aucunResultat')}
                  options={entites.map(e => ({
                    label: `${e.nom}${e.localite ? ` - ${e.localite}` : ''}${e.type ? ` · ${e.type}` : ''}`,
                    value: e.entiteConstatTrackingId,
                  }))}
                />
              </Form.Item>
            )}
            <Form.Item noStyle shouldUpdate>
              {() => (
                <div style={{ marginTop: -8, marginBottom: 12 }}>
                  <button type="button"
                    onClick={() => setPvEtranger(v => !v)}
                    style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: '#1a6b3c', textDecoration: 'underline' }}>
                    {pvEtranger ? t('nouveau.champs.choisirReferentiel') : t('nouveau.champs.saisirLibrement')}
                  </button>
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="sensCirculation"
              label={t('commun.sensCirculation')}
              rules={sensDerive ? [] : [{ required: true, message: t('nouveau.champs.sensRequis') }]}
              tooltip={t('nouveau.champs.sensAide')}
              extra={sensDerive ? t('nouveau.champs.sensDeduit') : undefined}
            >
              <Select placeholder={t('commun.sensCirculation')} disabled={sensDerive}>
                {PV_SENS_CIRCULATION.map(s => (
                  <Option key={s.value} value={s.value}>{s.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="dateAccidentPv"
              label={t('nouveau.champs.dateAccident')}
              rules={[{ required: true, message: t('nouveau.champs.dateAccidentRequise') }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={t('nouveau.champs.formatDate')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            {/* Facultative : un PV ancien, ou dont la date est illisible, doit
                rester enregistrable. Le backend ne l'exige pas non plus. */}
            <Form.Item
              name="dateEtablissementPv"
              label={t('commun.dateEtablissement')}
              tooltip={t('nouveau.champs.dateEtablissementAide')}
              rules={[{ validator: validerChronologie }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={t('nouveau.champs.formatDate')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="dateReceptionBncb"
              label={t('nouveau.champs.dateReception')}
              rules={[
                { required: true, message: t('nouveau.champs.dateReceptionRequise') },
                { validator: validerChronologie },
              ]}
            >
              <DatePicker
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={t('nouveau.champs.formatDate')}
                disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="lieuAccident"
          label={t('nouveau.champs.lieu')}
          rules={[{ required: true, message: t('nouveau.champs.lieuRequis') }]}
        >
          <TextArea
            rows={2}
            placeholder={t('nouveau.champs.lieuPlaceholder')}
            maxLength={500}
            showCount
          />
        </Form.Item>

        {/*  Section 2 - Contenu du PV  */}
        <Divider style={{ margin: '20px 0 16px' }} />
        <div className="cb-form-section-title">
          <CheckCircleFilled style={{ marginRight: 6 }} />
          {t('commun.contenuPv')}
          <Tooltip title={t('nouveau.contenu.aide')}>
            <InfoCircleOutlined style={{ marginLeft: 8, color: '#6b7280', fontSize: 13 }} />
          </Tooltip>
        </div>

        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          <Form.Item name="aCirconstances" noStyle>
            <ElementPvRadio
              label={t('nouveau.contenu.circonstances')}
              hint={t('nouveau.contenu.circonstancesAide')}
            />
          </Form.Item>
          <Form.Item name="aAuditions" noStyle>
            <ElementPvRadio
              label={t('nouveau.contenu.auditions')}
              hint={t('nouveau.contenu.auditionsAide')}
            />
          </Form.Item>
          <Form.Item name="aCroquis" noStyle>
            <ElementPvRadio
              label={t('nouveau.contenu.croquis')}
              hint={t('nouveau.contenu.croquisAide')}
            />
          </Form.Item>
        </Space>

        {/*  Section 3 - Provenance & association  */}
        <Divider style={{ margin: '20px 0 16px' }} />
        <div className="cb-form-section-title"><LinkOutlined style={{ marginRight: 6 }} />{t('commun.provenanceAssociation')}</div>

        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item
              name="provenance"
              label={t('nouveau.champs.source')}
              tooltip={t('nouveau.champs.sourceAide')}
            >
              <Select placeholder={t('nouveau.champs.sourcePlaceholder')} allowClear>
                {PV_PROVENANCES.map(p => (
                  <Option key={p.value} value={p.value}>{p.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={14}>
            <Form.Item
              name="detailProvenance"
              label={t('nouveau.champs.detailProvenance')}
              tooltip={t('nouveau.champs.detailProvenanceAide')}
            >
              <Input placeholder={t('nouveau.champs.detailProvenancePlaceholder')} maxLength={200} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={14}>
            <Form.Item
              name="sinistreTrackingId"
              label={t('commun.sinistreAssocie')}
              tooltip={defaultSinistreId
                ? t('nouveau.champs.sinistrePreselectionne')
                : t('nouveau.champs.sinistreLaisserVide')}
              extra={defaultSinistreId ? t('nouveau.champs.sinistrePreselectionneAuto') : null}
            >
              <Select
                showSearch
                allowClear={!defaultSinistreId}
                disabled={Boolean(defaultSinistreId)}
                placeholder={t('commun.rechercheSinistre')}
                suffixIcon={<SearchOutlined />}
                filterOption={false}
                onSearch={setSearchSinistre}
                notFoundContent={
                  searchSinistre.length < 2
                    ? t('commun.saisirDeuxCaracteres')
                    : t('commun.aucunSinistre')
                }
                options={sinistresOptions.map(s => ({
                  label: (
                    <span>
                      <strong style={{ color: CHARTE_COLORS.green }}>{s.numeroSinistreLocal}</strong>
                      <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 12 }}>
                        · {s.assureNomComplet || '-'}
                      </span>
                    </span>
                  ),
                  value: s.sinistreTrackingId,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={10}>
            <Form.Item
              name="referenceSinistreLiee"
              label={t('commun.referenceExterne')}
              tooltip={t('nouveau.champs.referenceExterneAide')}
            >
              <Input placeholder={t('nouveau.champs.referenceExternePlaceholder')} maxLength={50} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="remarques"
          label={t('nouveau.champs.remarques')}
        >
          <TextArea
            rows={3}
            placeholder={t('nouveau.champs.remarquesPlaceholder')}
            maxLength={1000}
            showCount
          />
        </Form.Item>

        {/*  Section 4 - Fichier scanné  */}
        <Divider style={{ margin: '20px 0 16px' }} />
        <div className="cb-form-section-title"><PaperClipOutlined style={{ marginRight: 6 }} />{t('commun.documentScanne')}</div>

        <GedFileField
          value={fichier}
          onChange={setFichier}
          maxSizeMo={20}
          hint={<Trans t={t} i18nKey="nouveau.document.hint" components={{ 1: <strong /> }} />}
        />

        {isReplace && (
          <>
            <Divider style={{ margin: '20px 0 16px' }} />
            <div className="cb-form-section-title"><EditOutlined style={{ marginRight: 6 }} />{t('nouveau.remplacement.motif')}</div>
            <TextArea
              value={motifRemplacement}
              onChange={(e) => setMotifRemplacement(e.target.value)}
              placeholder={t('nouveau.remplacement.motifPlaceholder')}
              rows={3}
              maxLength={500}
              showCount
            />
            <Alert
              showIcon
              type="info"
              style={{ marginTop: 12, borderRadius: 10 }}
              message={t('nouveau.remplacement.ancienObsolete')}
              description={t('nouveau.remplacement.description', { numero: replaceFromPv?.numeroPv || '', date: replaceFromPv?.dateReceptionBncb ? dayjs(replaceFromPv.dateReceptionBncb).format('DD/MM/YYYY') : '-' })}
            />
          </>
        )}

        {/* {!estComplet && (
          <Alert
            showIcon
            type="warning"
            style={{ marginTop: 16, borderRadius: 10 }}
            message="PV incomplet"
            description="En enregistrant, une relance automatique sera programmée vers la provenance indiquée pour obtenir les éléments manquants."
          />
        )} */}

        {/*  Footer  */}
        <div
          style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: 16,
            marginTop: 20,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <Button onClick={onClose} disabled={loading} danger>{t('commun.annuler')}</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            style={{ background: CHARTE_COLORS.green, borderColor: CHARTE_COLORS.green, minWidth: 140 }}
          >
            {isReplace ? t('nouveau.remplacerPv') : t('nouveau.enregistrerPv')}
          </Button>
        </div>
      </Form>
    </CBModal>
  );
}
